import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { negociosService, Negocio, CreateNegocioDto, UpdateNegocioDto } from '../src/services/negociosService';
import { empresasService, Empresa } from '../src/services/empresasService';
import { useAuth } from '../src/contexts/AuthContext';
import MapLocationPicker, { LocationResult } from '../components/MapLocationPicker';

interface NegociosProps {
  onClose: () => void;
}

const Negocios: React.FC<NegociosProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNegocio, setEditingNegocio] = useState<Negocio | null>(null);
  const [formData, setFormData] = useState<CreateNegocioDto>({
    empresaId: 0,
    nombreNegocio: '',
    fotoNegocioUrl: null,
    creadoPor: '',
    estaActivo: true,
    urlGoogleMaps: null,
    direccion: null,
    colonia: null,
    ciudad: null,
    estado: null,
    codigoPostal: null,
    categoria: null,
    icono: null,
  });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // Default to user's empresa if not SuperAdmin
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | 'all'>(user?.empresaId ?? 'all');
  const [sortConfig, setSortConfig] = useState<{ key: 'nombreNegocio' | 'estaActivo' | 'fechaCreacion'; direction: 'asc' | 'desc' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes disminuir { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(0.001); opacity: 0; } }
      @keyframes crecer { 0% { transform: scale(0.001); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const isDarkMode = () => {
    const theme = localStorage.getItem('theme');
    if (theme) return theme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const showAuthAlert = () => {
    const dark = isDarkMode();
    Swal.fire({
      icon: 'warning',
      title: 'Acceso Denegado',
      text: 'El usuario no está logueado',
      confirmButtonColor: '#f97316',
      background: dark ? '#1a1614' : '#ffffff',
      color: dark ? '#ffffff' : '#1a1614',
    }).then(() => {
      onClose();
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      showAuthAlert();
      return;
    }
    loadData();
  }, []);

  // Update selectedEmpresaId when user becomes available
  useEffect(() => {
    if (user?.empresaId && !isSuperAdmin) {
      setSelectedEmpresaId(user.empresaId);
    }
  }, [user?.empresaId, isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [negociosData, empresasData] = await Promise.all([
        negociosService.getAll(),
        empresasService.getAll()
      ]);
      setNegocios(negociosData);
      setEmpresas(empresasData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al cargar datos';
      if (errorMsg.includes('401') || errorMsg.includes('Unauthorized')) {
        showAuthAlert();
        return;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value as string;
    return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const filteredNegocios = useMemo(() => {
    const lowered = searchTerm.toLowerCase();
    let filtered = negocios.filter(n => n.nombreNegocio.toLowerCase().includes(lowered));
    if (selectedEmpresaId !== 'all') {
      filtered = filtered.filter(n => n.empresaId === selectedEmpresaId);
    }
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        if (sortConfig.key === 'estaActivo') {
          return (a.estaActivo === b.estaActivo ? 0 : a.estaActivo ? -1 : 1) * dir;
        }
        const aVal: any = (a as any)[sortConfig.key] ?? '';
        const bVal: any = (b as any)[sortConfig.key] ?? '';
        if (aVal < bVal) return -1 * dir;
        if (aVal > bVal) return 1 * dir;
        return 0;
      });
    }
    return filtered;
  }, [negocios, searchTerm, sortConfig, selectedEmpresaId]);

  const handleSort = (key: 'nombreNegocio' | 'estaActivo' | 'fechaCreacion') => {
    setSortConfig(prev => {
      if (prev && prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        
      }
      return { key, direction: 'asc' };
    });
  };

  const handleCreate = () => {
    setEditingNegocio(null);
    // Default to selectedEmpresaId, or first active empresa for SuperAdmin
    const defaultEmpresaId = isSuperAdmin 
      ? (selectedEmpresaId !== 'all' ? selectedEmpresaId : (empresas.find(e => e.activa)?.empresaId ?? 0))
      : (user?.empresaId ?? 0);
    setFormData({
      empresaId: defaultEmpresaId,
      nombreNegocio: '',
      fotoNegocioUrl: null,
      creadoPor: localStorage.getItem('username') || '',
      estaActivo: true,
      urlGoogleMaps: null,
      direccion: null,
      colonia: null,
      ciudad: null,
      estado: null,
      codigoPostal: null,
      categoria: null,
      icono: null,
    });
    setSelectedFile(null);
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const getFullImageUrl = (url: string | null) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
    return `${apiBase}${url}`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleEdit = (negocio: Negocio) => {
    setEditingNegocio(negocio);
    setFormData({
      empresaId: negocio.empresaId,
      nombreNegocio: negocio.nombreNegocio,
      fotoNegocioUrl: negocio.fotoNegocioUrl,
      creadoPor: negocio.creadoPor,
      estaActivo: negocio.estaActivo,
      urlGoogleMaps: negocio.urlGoogleMaps,
      direccion: negocio.direccion,
      colonia: negocio.colonia,
      ciudad: negocio.ciudad,
      estado: negocio.estado,
      codigoPostal: negocio.codigoPostal,
      categoria: negocio.categoria,
      icono: negocio.icono,
    });
    setSelectedFile(null);
    setImagePreview(getFullImageUrl(negocio.fotoNegocioUrl));
    setIsModalOpen(true);
  };

  const handleMapSelect = (location: LocationResult) => {
    setFormData(prev => ({
      ...prev,
      urlGoogleMaps: location.urlGoogleMaps,
      direccion: location.direccion,
      colonia: location.colonia,
      ciudad: location.ciudad,
      estado: location.estado,
      codigoPostal: location.codigoPostal,
    }));
    setShowMapPicker(false);
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setEditingNegocio(null);
    }, 300);
  };

  const handleDelete = async (id: number) => {
    const negocio = negocios.find(n => n.negocioId === id);
    const nombre = negocio?.nombreNegocio || `ID ${id}`;
    const result = await Swal.fire({
      title: '¿Eliminar negocio?',
      html: `Esta acción eliminará el negocio <strong>${nombre}</strong>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: 'rgba(15,23,42,0.9)',
      color: '#fff',
      backdrop: false
    });
    if (!result.isConfirmed) return;
    try {
      await negociosService.delete(id);
      setNegocios(negocios.filter(n => n.negocioId !== id));
      Swal.fire({ title: 'Negocio eliminado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.empresaId === 0) {
      setError('Debe seleccionar una empresa');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let savedNegocio: Negocio;
      if (editingNegocio) {
        const updateData: UpdateNegocioDto = { ...formData };
        savedNegocio = await negociosService.update(editingNegocio.negocioId, updateData);
      } else {
        savedNegocio = await negociosService.create(formData);
      }

      if (selectedFile) {
        const uploadResult = await negociosService.uploadImage(savedNegocio.negocioId, selectedFile);
        savedNegocio = { ...savedNegocio, fotoNegocioUrl: uploadResult.fotoNegocioUrl };
      }

      if (editingNegocio) {
        setNegocios(negocios.map(n => n.negocioId === savedNegocio.negocioId ? savedNegocio : n));
        Swal.fire({ title: 'Negocio actualizado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false });
      } else {
        setNegocios([savedNegocio, ...negocios]);
        Swal.fire({ title: 'Negocio creado', icon: 'success', background: 'rgba(15,23,42,0.85)', color: '#fff', confirmButtonColor: '#3b82f6', timer: 2000, timerProgressBar: true, backdrop: false });
      }
      setEditingNegocio(null);
      closeModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 bg-white dark:bg-background-dark overflow-auto" style={{ animation: 'crecer 0.3s ease-out' }}>
      <div className="dark:bg-background-dark text-gray-900 dark:text-white px-4 sm:px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="flex items-center gap-2 text-gray-900 dark:text-white/80 hover:text-gray-900 dark:hover:text-white transition-colors">
            <span className="material-symbols-outlined">arrow_back</span>
            <span className="hidden sm:inline">Volver</span>
          </button>
          <div>
            <h1 className="text-2xl text-secondary dark:text-white ">Negocios</h1>
            <p className="text-gray-900 dark:text-white text-sm hidden sm:block">Administra los negocios registrados</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="bg-[#307CAF] dark:bg-slate-800 text-white px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
              <input type="text" placeholder="Buscar por nombre..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent" />
            </div>
          </div>
          {isSuperAdmin && (
            <div className="sm:w-64">
              <select value={selectedEmpresaId} onChange={(e) => setSelectedEmpresaId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                className="w-full px-3 py-2 border border-white/20 dark:border-slate-600 rounded-full bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-white/50 focus:border-transparent">
                <option value="all">Todas las empresas</option>
                {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <span>{filteredNegocios.length} negocios</span>
            <button onClick={handleCreate} className="inline-flex items-center gap-1 px-4 py-2 bg-white text-[#307CAF] dark:bg-slate-600 dark:text-white rounded-full font-semibold hover:bg-white/90 dark:hover:bg-slate-500 transition">
              <span className="material-symbols-outlined text-base">add</span>Nuevo Negocio
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-600 dark:text-red-400 text-sm">{error}</div>
        )}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <div className="animate-spin w-8 h-8 border-4 border-[#307CAF] border-t-transparent rounded-full mx-auto mb-4"></div>Cargando negocios...
            </div>
          ) : filteredNegocios.length === 0 ? (
            <div className="p-12 text-center text-slate-500 dark:text-slate-400">
              <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600 mb-4 block">domain</span>No se encontraron negocios con ese criterio.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-[#307CAF] text-white dark:bg-slate-700">
                  <tr>
                    <th onClick={() => handleSort('nombreNegocio')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Nombre{sortConfig?.key === 'nombreNegocio' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Empresa</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ubicación</th>
                    <th onClick={() => handleSort('fechaCreacion')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Registro{sortConfig?.key === 'fechaCreacion' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th onClick={() => handleSort('estaActivo')} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-white/10 dark:hover:bg-slate-600">
                      <div className="flex items-center gap-1">Estado{sortConfig?.key === 'estaActivo' && <span className="material-symbols-outlined text-sm">{sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {filteredNegocios.map((negocio) => (
                    <tr key={negocio.negocioId} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {negocio.fotoNegocioUrl ? (
                            <img src={getFullImageUrl(negocio.fotoNegocioUrl)!} alt={negocio.nombreNegocio} className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-[#307CAF]/10 flex items-center justify-center">
                              <span className="text-[#307CAF] font-bold text-sm">{negocio.nombreNegocio.substring(0, 2).toUpperCase()}</span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white">{negocio.nombreNegocio}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{negocio.ciudad || ''} {negocio.estado || ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{(negocio as any).nombreEmpresa || empresas.find(e => e.empresaId === negocio.empresaId)?.nombreEmpresa || `ID: ${negocio.empresaId}`}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{negocio.urlGoogleMaps ? (
                        <a href={negocio.urlGoogleMaps} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 hover:underline">Ver en Maps</a>
                      ) : (
                        <span className="text-slate-400">Sin enlace</span>
                      )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">{formatDate((negocio as any).fechaCreacion)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${negocio.estaActivo ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                          <span className="material-symbols-outlined text-sm">{negocio.estaActivo ? 'verified' : 'visibility_off'}</span>{negocio.estaActivo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleEdit(negocio)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-1 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">
                            <span className="material-symbols-outlined text-sm">edit</span>Editar
                          </button>
                          <button onClick={() => handleDelete(negocio.negocioId)} className="inline-flex items-center gap-1 rounded-lg border border-transparent px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                            <span className="material-symbols-outlined text-sm">delete</span>Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="relative w-full max-w-2xl rounded-2xl bg-white/95 dark:bg-slate-800 p-6 shadow-xl backdrop-blur-sm border border-gray-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto" style={{ animation: isModalClosing ? 'disminuir 0.3s ease-out' : 'crecer 0.3s ease-out' }}>
            <button onClick={closeModal} className="absolute right-3 top-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white">
              <span className="material-symbols-outlined">close</span>
            </button>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">{editingNegocio ? 'Editar Negocio' : 'Crear Negocio'}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{editingNegocio ? 'Modifica los datos del negocio.' : 'Añade los datos del nuevo negocio.'}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Empresa *</label>
                  <select value={formData.empresaId} onChange={(e) => setFormData({ ...formData, empresaId: Number(e.target.value) })} required disabled={!!editingNegocio}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]">
                    <option value={0}>Seleccionar empresa...</option>
                    {empresas.map((empresa) => (<option key={empresa.empresaId} value={empresa.empresaId}>{empresa.nombreEmpresa}</option>))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Nombre del Negocio *</label>
                <input type="text" value={formData.nombreNegocio} onChange={(e) => setFormData({ ...formData, nombreNegocio: e.target.value })} required maxLength={200}
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
              </div>
              {!editingNegocio && (
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Creado Por *</label>
                  <input type="text" value={formData.creadoPor || ''} onChange={(e) => setFormData({ ...formData, creadoPor: e.target.value })} required maxLength={100}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Foto del Negocio (opcional)</label>
                <div className="flex items-center gap-4">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="h-16 w-16 rounded-lg object-cover border-2 border-slate-200 dark:border-slate-700" />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-[#307CAF]/10 flex items-center justify-center">
                      <span className="material-symbols-outlined text-[#307CAF] text-2xl">store</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-gray-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors">
                      <span className="material-symbols-outlined text-base">upload</span>
                      {selectedFile ? selectedFile.name : 'Seleccionar imagen'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">JPG, PNG o WEBP. Máx 5 MB.</p>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300">Ubicación</label>
                  <button
                    type="button"
                    onClick={() => setShowMapPicker(true)}
                    className="inline-flex items-center gap-1 text-sm text-[#307CAF] hover:text-[#307CAF]/80 font-medium transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">map</span>
                    Seleccionar en mapa
                  </button>
                </div>
                <input type="url" value={formData.urlGoogleMaps || ''} onChange={(e) => setFormData({ ...formData, urlGoogleMaps: e.target.value || null })} maxLength={500}
                  placeholder="Enlace de Google Maps"
                  className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Dirección</label>
                  <input type="text" value={formData.direccion || ''} onChange={(e) => setFormData({ ...formData, direccion: e.target.value || null })} maxLength={200}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Colonia</label>
                  <input type="text" value={formData.colonia || ''} onChange={(e) => setFormData({ ...formData, colonia: e.target.value || null })} maxLength={100}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Ciudad</label>
                    <input type="text" value={formData.ciudad || ''} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value || null })} maxLength={100}
                      className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Estado</label>
                    <input type="text" value={formData.estado || ''} onChange={(e) => setFormData({ ...formData, estado: e.target.value || null })} maxLength={100}
                      className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Código Postal</label>
                  <input type="text" value={formData.codigoPostal || ''} onChange={(e) => setFormData({ ...formData, codigoPostal: e.target.value || null })} maxLength={10}
                    className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Categoría</label>
                    <input type="text" value={formData.categoria || ''} onChange={(e) => setFormData({ ...formData, categoria: e.target.value || null })} maxLength={100}
                      placeholder="Ej: Comida, Farmacia, etc."
                      className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#307CAF] dark:text-slate-300 mb-1">Icono</label>
                    <input type="text" value={formData.icono || ''} onChange={(e) => setFormData({ ...formData, icono: e.target.value || null })} maxLength={100}
                      placeholder="Ej: restaurant, local_pharmacy"
                      className="w-full rounded-lg border border-gray-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700 px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#307CAF]" />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-xl border border-gray-200 dark:border-slate-600 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#307CAF] dark:text-slate-200">Estado</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Define si el negocio está activo en el sistema.</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <span className="mr-3 text-sm text-slate-500 dark:text-slate-400">{formData.estaActivo ? 'Activo' : 'Inactivo'}</span>
                  <input type="checkbox" className="sr-only" checked={formData.estaActivo} onChange={(e) => setFormData({ ...formData, estaActivo: e.target.checked })} />
                  <span className={`relative inline-block h-6 w-11 rounded-full transition ${formData.estaActivo ? 'bg-[#307CAF]' : 'bg-slate-400'}`}>
                    <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition ${formData.estaActivo ? 'translate-x-5' : ''}`} />
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" className="rounded-lg border border-gray-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" onClick={closeModal}>Cancelar</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#307CAF]/90 disabled:opacity-60">
                  {saving ? (<><span className="material-symbols-outlined animate-spin">progress_activity</span>Guardando...</>) : (<><span className="material-symbols-outlined">save</span>Guardar</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showMapPicker && (
        <MapLocationPicker
          onSelect={handleMapSelect}
          onClose={() => setShowMapPicker(false)}
        />
      )}
    </div>
  );
};

export default Negocios;
